import React, {useEffect, useState} from "react";
import AppBar from "../components/app_bar/AppBar";
import Drawer from "../components/Drawer";
import RegularVideo from "../components/RegularVideo";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faYoutube, faBilibili, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { 
    Typography, Container, CssBaseline, Box, Toolbar, Link, Chip, Grid, List, ListItem, ListItemText, ListItemIcon,
    Divider, Tooltip,
} from '@mui/material';
import { useParams } from "react-router-dom";
import PersonIcon from '@mui/icons-material/Person';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DescriptionIcon from '@mui/icons-material/Description';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import LabelIcon from '@mui/icons-material/Label';
import { useLocation } from "react-router-dom";
import Cookies from 'js-cookie';
import * as CONSTANTS from "../constants";
import "../styles/WazarenDetails.css";
import RadioIconButtonGroup from '../components/RadioIconButtonGroup'
import NotFound from "./NotFound";

import { view_wazaren } from "../services/topic_view";


const WazarenDetails = () => {

    const location = useLocation();

    const [wazaren, setWazaren] = useState(null);
    let {lang, wazarenId} = useParams();

    const [videoLink, setVideoLink] = useState(''); // Default to a YouTube URL
    const [videoSource, setVideoSource] = useState('')

    const [drawerOpen, setDrawerOpen] = useState(false);
            
    const handleDrawerOpen = (open) => {
        if (open === undefined) {
            setDrawerOpen(!drawerOpen)
        } else {
            setDrawerOpen(open);
        }
    }

    const handleVideoSourceChange = (newValue) => {
        if (newValue !== videoSource) {
            setVideoSource(newValue);
            switch (newValue)
            {
                case 'youtube':
                    setVideoLink(wazaren.video_source.youtube);
                    break;
                case 'bilibili':
                    setVideoLink(wazaren.video_source.bilibili);
                    break;
                case 'x':
                    setVideoLink(wazaren.video_source.x);
                    break;
            }
        }
    };

    const fetchWazaren = async () => {
        const [status, result] = await view_wazaren(lang, wazarenId.toString());
        if (status === 200) {
            setWazaren(result);
            // Select default video source
            if (result.video_source.youtube != '') {
                setVideoLink(result.video_source.youtube);
                setVideoSource('youtube');
            } else if (result.video_source.bilibili != '') {
                setVideoLink(result.video_source.bilibili);
                setVideoSource('bilibili');
            } else if (result.video_source.x != '')  {
                setVideoLink(result.video_source.x);
                setVideoSource('x');
            }
        }
    }

    // send api request for url changes
    useEffect(() => {
        fetchWazaren();
    }, [location.pathname, location.search]);
    

    if (wazaren) {

        document.title = `${wazaren.name} | ${CONSTANTS.DRAWER.WAZAREN[lang]} - Wotagei Zone`

        return (
            <Box>
                <CssBaseline />
                <AppBar lang={lang} handleDrawerOpen={handleDrawerOpen}/>
                <Box sx={{ display: 'flex' }}>
                    <Drawer lang={lang} drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
                    <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                        <Toolbar />
                        <Container maxWidth="md">
                            {/* Breadcrumb and Title */}
                            <Typography>
                                <Link
                                    href={`/${lang}/wazaren`}
                                    underline="hover"
                                    color={"textPrimary"}
                                >
                                    {CONSTANTS.DRAWER.WAZAREN[lang]}
                                </Link>
                                {` > ${wazaren.name}`}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Typography 
                                variant="h4" 
                                gutterBottom
                                mt={4}
                                sx={{
                                    textAlign: { xs: 'center', md: 'left' },
                                    fontWeight: 700
                                }}
                                mb={4}
                            >
                                {wazaren.name}
                            </Typography>

                            {/* Main Info Section */}
                            <Box sx={{ mb: 4 }}>
                                <Grid container spacing={3}>
                                    {/* Creator Name */}
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <PersonIcon />
                                            <Typography variant="subtitle2" fontWeight={600}>{CONSTANTS.WAZAREN_DETAILS.CREATOR[lang]}:</Typography>
                                            <Typography variant="body2">{wazaren.creator_name}</Typography>
                                        </Box>
                                    </Grid>
                                    {/* Editor Name */}
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <PersonIcon />
                                            <Typography variant="subtitle2" fontWeight={600}>{CONSTANTS.WAZAREN_DETAILS.EDITOR[lang]}:</Typography>
                                            <Typography variant="body2">{wazaren.editor_name}</Typography>
                                        </Box>
                                    </Grid>
                                    {/* Date Created */}
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <ScheduleIcon />
                                            <Typography variant="subtitle2" fontWeight={600}>{CONSTANTS.WAZAREN_DETAILS.DATE_CREATED[lang]}:</Typography>
                                            <Typography variant="body2">{wazaren.date_created}</Typography>
                                        </Box>
                                    </Grid>
                                    {/* Tags */}
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                            <LabelIcon />
                                            <Typography variant="subtitle2" fontWeight={600}>{CONSTANTS.WAZAREN_FILTER.TAGS[lang]}:</Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {wazaren.tags.map((tag) => (
                                                    <Chip
                                                        key={tag.toString()} 
                                                        label={CONSTANTS.WAZAREN_TAGS[tag][lang].toString()}
                                                        variant="outlined"
                                                        sx={{
                                                            borderRadius: '0.5rem',
                                                            borderColor: '#ccc',
                                                            fontSize: '0.85rem',
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    </Grid>
                                    {/* Description */}
                                    <Grid item xs={12} md={12}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <DescriptionIcon />
                                            <Typography variant="subtitle2" fontWeight={600}>{CONSTANTS.WAZAREN_DETAILS.DESCRIPTION[lang]}:</Typography>
                                            <Typography variant="body2">{wazaren.description}</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                            <Divider sx={{ my: 2 }} />

                            {/* Video Section */}
                            <Box sx={{ mt: 4, mb: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <VideoLibraryIcon />
                                    <Typography variant="h6" fontWeight={700} ml={1}>{CONSTANTS.WAZAREN_DETAILS.VIDEO[lang]}</Typography>
                                </Box>
                                <Box mb={2}>
                                    <RadioIconButtonGroup 
                                        hook={videoSource}
                                        setHook={handleVideoSourceChange}
                                        buttonValues={['youtube', 'bilibili', 'x']}
                                        buttonIcons={{
                                            'youtube': <FontAwesomeIcon icon={faYoutube} />, 'bilibili': <FontAwesomeIcon icon={faBilibili} />, 'x': <FontAwesomeIcon icon={faXTwitter} />
                                        }}
                                        buttonDisableds={{
                                            'youtube': wazaren.video_source? wazaren.video_source.youtube == '': true,
                                            'bilibili': wazaren.video_source? wazaren.video_source.bilibili == '': true,
                                            'x': wazaren.video_source? wazaren.video_source.x == '': true,
                                        }}
                                        tooltipValues={{
                                            'youtube': 'YouTube',
                                            'bilibili': "Bilibili",
                                            'x': "X"
                                        }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Typography fontWeight={600}>備注:</Typography>
                                    <Typography ml={1}>{wazaren.video_remark?wazaren.video_remark[videoSource]: ''}</Typography>
                                </Box>
                                <RegularVideo 
                                    url={videoLink}
                                    videoSource={videoSource}
                                />
                            </Box>
                            <Divider sx={{ my: 2 }} />

                            {/* Key Movements Section */}
                            <Box sx={{ mt: 4, mb: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <DirectionsRunIcon />
                                    <Typography variant="h6" fontWeight={700} ml={1}>關鍵動作</Typography>
                                </Box>
                                <List dense={false}>
                                    {wazaren.key_movements.map((key_movement, idx) => (
                                        <React.Fragment key={idx}>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <LabelIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={key_movement.name}
                                                    secondary={key_movement.description}
                                                />
                                            </ListItem>
                                            <Divider variant="inset" component="li" />
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                        </Container>
                    </Box>
                </Box>
            </Box>
        );
    }
    
};
 
export default WazarenDetails;